import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Chip,
  createTheme,
  defaultVariantColorsResolver,
  Drawer,
  Input,
  Menu,
  Modal,
  NavLink,
  NumberInput,
  Popover,
  Select,
  Table,
  Tooltip,
  type CSSVariablesResolver,
  type VariantColorsResolver,
} from '@mantine/core'

import { colorTokens, motionTokens, shapeTokens, typeTokens } from './theme/tokens'

/**
 * "Deep Tide" (docs/design/identity.md §3–§5): Manrope for display numbers/headlines/titles, Inter for body/label,
 * warm sand and night-navy surfaces, navy (light) / teal (dark) for action, flat lifted cards. Values come from
 * [colorTokens]/[typeTokens]/[shapeTokens] (tools/design-tokens/generate.py), so both platforms match.
 */
const HEADING_FONT = `${typeTokens.headline.family}, system-ui, -apple-system, "Segoe UI", sans-serif`
const BODY_FONT = `${typeTokens.body.family}, system-ui, -apple-system, "Segoe UI", sans-serif`

/**
 * Deep Tide's primary isn't one hue at different shades (Mantine's usual model): light mode's primary is navy,
 * dark mode's is teal (identity.md §3). "tide" anchors exactly the two shades `primaryShade` below selects —
 * index 9 (light) to the navy, index 3 (dark) to the teal — and fills the rest of Mantine's required 10-shade
 * array with those same two documented colours rather than inventing intermediate tints.
 */
const TIDE = [
  colorTokens.dark.actionPrimary,
  colorTokens.dark.actionPrimary,
  colorTokens.dark.actionPrimary,
  colorTokens.dark.actionPrimary,
  colorTokens.dark.actionPrimary,
  colorTokens.light.actionPrimary,
  colorTokens.light.actionPrimary,
  colorTokens.light.actionPrimary,
  colorTokens.light.actionPrimary,
  colorTokens.light.actionPrimary,
] as const

/**
 * Mantine works out filled text colour once, from the light shade, so the dark theme's light teal (tide-3) would get
 * white text. Filled "tide" instead reads `--mt-on-primary`, which styles.css sets per scheme (identity.md §3).
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  // "danger": the quiet red of a destructive action (delete, remove), i.e. subtle red, named once here.
  if (input.variant === 'danger') return defaultVariantColorsResolver({ ...input, variant: 'subtle', color: 'red' })
  const colors = defaultVariantColorsResolver(input)
  const primary = input.color === undefined || input.color === 'tide'
  if (input.variant === 'filled' && primary) return { ...colors, color: 'var(--mt-on-primary)' }
  // Secondary buttons are an outlined pill, 1.5 px in the primary colour (identity.md §5), not Mantine's 1 px default.
  if (input.variant === 'outline') return { ...colors, border: colors.border.replace(/^[^ ]+/, '1.5px') }
  return colors
}

export const theme = createTheme({
  primaryColor: 'tide',
  variantColorResolver,
  primaryShade: { light: 9, dark: 3 },
  autoContrast: true,
  focusRing: 'auto',
  // One focus ring everywhere (§11.2 "Focus"), and `respectReducedMotion` so Mantine's own transitions shorten
  // under `prefers-reduced-motion` (§11.4) without a bespoke reduced-motion prop on every component.
  focusClassName: 'mt-focus-ring',
  respectReducedMotion: true,
  fontFamily: BODY_FONT,
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  headings: {
    fontFamily: HEADING_FONT,
    fontWeight: '600',
    sizes: {
      // Owner decision, 24 Oct 2026 (§12 #5): M3's small-app-bar title size (titleLarge, 22/28), overriding the
      // earlier "keep 24" recommendation. PageHeader's <Title order={1}> is every page's title.
      h1: { fontSize: '22px', lineHeight: '28px' },
      h2: { fontSize: '20px', lineHeight: '28px' },
      h3: { fontSize: '14px', lineHeight: '20px' },
      h4: { fontSize: '16px', lineHeight: '24px' },
      h5: { fontSize: '14px', lineHeight: '20px' },
    },
  },
  spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
  // md/xl are the identity's card/sheet radii (docs/design/identity.md §5); xs/sm/lg aren't named there, so they
  // keep their existing values (closest documented step either side).
  radius: { xs: '4px', sm: '8px', md: `${shapeTokens.card}px`, lg: '16px', xl: `${shapeTokens.sheet}px` },
  defaultRadius: 'md',
  colors: {
    gray: ['#F8F7F4', '#F3F2EE', '#E9E8E3', '#E1E0D9', '#C3C2B7', '#A3A29A', '#898781', '#52514E', '#2E2D2B', '#0B0B0B'],
    dark: ['#F0EFEC', '#C3C2B7', '#A3A29A', '#898781', '#383835', '#2C2C2A', '#1A1A19', '#111110', '#0B0B0A', '#050505'],
    tide: TIDE,
  },
  components: {
    // Flat and tonal, lifted with the identity's soft shadow instead of a border (docs/design/identity.md §5;
    // styles.css's `.mantine-Card-root` sets `box-shadow: var(--mt-token-shadow-card)`, none in dark mode).
    Card: Card.extend({ defaultProps: { bg: 'var(--mt-card)', withBorder: false, padding: 'md', radius: 'md' } }),
    // Pill radius, 48 px minimum tap target (identity.md §5/§11, WCAG 2.5.5) — skipped for the ops panel's own
    // "compact-*" sizes (phase 5's compact variant, not a tap target meant to be reachable at arm's length).
    Button: Button.extend({
      defaultProps: { radius: 'xl' },
      styles: (theme, props) => {
        const root: Record<string, unknown> = typeof props.size === 'string' && props.size.startsWith('compact') ? {} : { minHeight: 48 }
        // identity.md §5: a secondary button is an outlined pill, not Mantine's filled "light" tint -- every
        // secondary action across the app already asks for `variant="light"` (Library, Edit layout, "+250 ml" ...),
        // so this is fixed once here, by reusing the theme's own (corrected) outline colours, rather than touching
        // every one of those call sites.
        if (props.variant === 'light') {
          const outline = theme.variantColorResolver({
            color: props.color || theme.primaryColor,
            theme,
            variant: 'outline',
            autoContrast: props.autoContrast,
          })
          Object.assign(root, { '--button-bg': outline.background, '--button-hover': outline.hover, '--button-color': outline.color, '--button-bd': outline.border })
        }
        return { root }
      },
    }),
    // The sheet radius (docs/design/identity.md §5), the same shape M3 gives both dialogs and bottom sheets; "Crisp"
    // motion (§9) for the open/close, not an M3 spring.
    Modal: Modal.extend({
      defaultProps: { radius: 'xl', centered: true, transitionProps: { duration: motionTokens.standardMs, timingFunction: motionTokens.standardEasing } },
    }),
    // Owner decision, 24 Oct 2026 (§12 #6, #11): M3's 4 dp outlined-field corner, overriding the earlier "keep 8"
    // recommendation.
    Input: Input.extend({ defaultProps: { radius: 'xs' } }),
    // Fully round, selected = filled primary (identity.md §5; Mantine's own checked-chip style already fills primary).
    Chip: Chip.extend({ defaultProps: { radius: 'xl' } }),
    // "Crisp" motion (identity.md §9) for sheets, same as the Modal above.
    Drawer: Drawer.extend({ defaultProps: { transitionProps: { duration: motionTokens.standardMs, timingFunction: motionTokens.standardEasing } } }),
    Tooltip: Tooltip.extend({ defaultProps: { transitionProps: { duration: 150, transition: 'fade' }, openDelay: 200 } }),
    // Either "." or "," types the decimal point (keyboards differ by locale); it shows as "." and only one is allowed.
    NumberInput: NumberInput.extend({ defaultProps: { decimalSeparator: '.', allowedDecimalSeparators: ['.', ','] } }),
    // A pick list always has an answer; the few that may be cleared say allowDeselect.
    Select: Select.extend({ defaultProps: { allowDeselect: false } }),
    // Icon buttons are quiet grey unless they say otherwise (a red delete, a bordered day arrow).
    ActionIcon: ActionIcon.extend({ defaultProps: { variant: 'subtle', color: 'gray' } }),
    Menu: Menu.extend({ defaultProps: { position: 'bottom-end', withinPortal: true, shadow: 'md' } }),
    Popover: Popover.extend({ defaultProps: { shadow: 'md' } }),
    // Navigation rows: the pill of the side navigation and the settings sections.
    NavLink: NavLink.extend({ defaultProps: { variant: 'light' }, styles: { root: { borderRadius: 'var(--mantine-radius-xl)', minHeight: 40 } } }),
    Badge: Badge.extend({ defaultProps: { variant: 'light' } }),
    // Figures line up in every table (§4.7).
    Table: Table.extend({ classNames: { table: 'tabular' } }),
  },
})

/** Surfaces and text roles (docs/design/identity.md §3); Mantine's own defaults are cool greys. */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-body': colorTokens.light.backgroundDefault,
    '--mantine-color-text': colorTokens.light.textPrimary,
    '--mantine-color-dimmed': colorTokens.light.textSecondary,
    '--mantine-color-default-border': colorTokens.light.borderDivider,
    '--mantine-primary-color-contrast': colorTokens.light.actionOnPrimary,
  },
  dark: {
    '--mantine-color-body': colorTokens.dark.backgroundDefault,
    '--mantine-color-text': colorTokens.dark.textPrimary,
    '--mantine-color-dimmed': colorTokens.dark.textSecondary,
    '--mantine-color-default-border': colorTokens.dark.borderDivider,
    '--mantine-primary-color-contrast': colorTokens.dark.actionOnPrimary,
  },
})
